import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full h-12 px-4 rounded-xl bg-[#ffffff] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors";
const area = `${input} h-28 py-3 resize-none`;
const lbl = "block font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a] mb-1.5";

export const BUSINESS_TYPES = ["Boutique / concept store", "Streetwear retailer", "Online store", "Marketplace seller", "Distributor / wholesaler", "Mosque / community shop", "Pop-up / events", "Other"];
export const YEARS = ["Just starting", "Under 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"];
export const REVENUE = ["Under $50k", "$50k - $150k", "$150k - $500k", "$500k - $1M", "$1M - $5M", "$5M+", "Prefer not to say"];
export const VOLUME = ["10-25 pieces", "25-50 pieces", "50-100 pieces", "100-250 pieces", "250+ pieces"];
export const CHANNELS = ["Physical store", "Own website", "Instagram / TikTok shop", "Marketplaces (Etsy, Amazon…)", "Events & pop-ups", "Wholesale to other stores"];
export const TIMELINE = ["As soon as possible", "Within 1 month", "1-3 months", "3-6 months", "Just exploring"];
export const LOCATIONS = ["1", "2-3", "4-10", "10+", "Online only"];

const blank = {
  business_name: "", contact_name: "", role: "", email: "", phone: "", website: "", instagram: "",
  business_type: "", years_in_business: "", country: "", city: "", address: "", locations: "",
  annual_revenue: "", sales_channels: [], where_sell: "", monthly_volume: "", brands_carried: "", customer_profile: "",
  why_deen: "", launch_timeline: "", notes: "",
};

const Section = ({ n, title, sub, children }) => (
  <fieldset className="rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6 sm:p-8">
    <legend className="sr-only">{title}</legend>
    <div className="flex items-baseline gap-3 mb-6">
      <span className="font-mono-d text-[10px] text-[#ff2a85] tracking-[0.2em]">0{n}</span>
      <div>
        <h2 className="font-display font-extrabold uppercase text-base md:text-lg leading-none">{title}</h2>
        {sub && <p className="text-sm text-[#5f5e5a] mt-1.5">{sub}</p>}
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">{children}</div>
  </fieldset>
);

const Field = ({ id, label, full, children }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label htmlFor={id} className={lbl}>{label}</label>
    {children}
  </div>
);

const Select = ({ id, value, onChange, options, placeholder = "Select…" }) => (
  <select id={id} data-testid={id} required className={`${input} appearance-none`} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const WholesaleForm = () => {
  const [f, setF] = useState(blank);
  const [state, setState] = useState("idle");
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const toggleChannel = (c) => set({ sales_channels: f.sales_channels.includes(c) ? f.sales_channels.filter((x) => x !== c) : [...f.sales_channels, c] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.sales_channels.length) return toast.error("Tick at least one sales channel.");
    setState("sending");
    try {
      await axios.post(`${API}/wholesale/apply`, f);
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Could not send your application. Try again.");
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="wholesale-success" className="rounded-3xl bg-[#0a0a0b] text-white p-8 sm:p-10">
        <span className="h-12 w-12 rounded-full bg-[#ff2a85] flex items-center justify-center"><Check size={20} strokeWidth={3} /></span>
        <h2 className="mt-6 font-display font-extrabold uppercase text-2xl leading-none">Application received</h2>
        <p className="mt-4 text-white/75 leading-relaxed max-w-md">JazakAllah khair, {f.contact_name.split(" ")[0]}. We review every stockist personally and reply to <span className="text-white">{f.email}</span> within 3-5 business days with wholesale pricing, minimums and next steps.</p>
        <p className="mt-6 font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Deen over Dunya</p>
      </div>
    );
  }

  return (
    <form data-testid="wholesale-form" onSubmit={submit} className="space-y-5">
      <Section n={1} title="Who you are" sub="Tell us about your business and the best person to talk to.">
        <Field id="ws-business-name" label="Business name"><input id="ws-business-name" data-testid="ws-business-name" className={input} required minLength={2} value={f.business_name} onChange={(e) => set({ business_name: e.target.value })} /></Field>
        <Field id="ws-business-type" label="Business type"><Select id="ws-business-type" value={f.business_type} onChange={(v) => set({ business_type: v })} options={BUSINESS_TYPES} /></Field>
        <Field id="ws-contact-name" label="Your name"><input id="ws-contact-name" data-testid="ws-contact-name" className={input} required minLength={2} value={f.contact_name} onChange={(e) => set({ contact_name: e.target.value })} /></Field>
        <Field id="ws-role" label="Your role (optional)"><input id="ws-role" data-testid="ws-role" className={input} placeholder="Owner, buyer, manager…" value={f.role} onChange={(e) => set({ role: e.target.value })} /></Field>
        <Field id="ws-email" label="Email"><input id="ws-email" data-testid="ws-email" className={input} required type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
        <Field id="ws-phone" label="Phone / WhatsApp (optional)"><input id="ws-phone" data-testid="ws-phone" className={input} value={f.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
        <Field id="ws-website" label="Website (optional)"><input id="ws-website" data-testid="ws-website" className={input} placeholder="https://" value={f.website} onChange={(e) => set({ website: e.target.value })} /></Field>
        <Field id="ws-instagram" label="Instagram / TikTok (optional)"><input id="ws-instagram" data-testid="ws-instagram" className={input} placeholder="@yourstore" value={f.instagram} onChange={(e) => set({ instagram: e.target.value })} /></Field>
        <Field id="ws-years" label="Years in business"><Select id="ws-years" value={f.years_in_business} onChange={(v) => set({ years_in_business: v })} options={YEARS} /></Field>
        <Field id="ws-locations" label="Number of locations"><Select id="ws-locations" value={f.locations} onChange={(v) => set({ locations: v })} options={LOCATIONS} /></Field>
      </Section>

      <Section n={2} title="Where you are" sub="So we can plan shipping, duties and territory.">
        <Field id="ws-country" label="Country"><input id="ws-country" data-testid="ws-country" className={input} required minLength={2} value={f.country} onChange={(e) => set({ country: e.target.value })} /></Field>
        <Field id="ws-city" label="City"><input id="ws-city" data-testid="ws-city" className={input} required value={f.city} onChange={(e) => set({ city: e.target.value })} /></Field>
        <Field id="ws-address" label="Store / warehouse address (optional)" full><input id="ws-address" data-testid="ws-address" className={input} value={f.address} onChange={(e) => set({ address: e.target.value })} /></Field>
      </Section>

      <Section n={3} title="Your business" sub="Revenue and volume help us set the right wholesale tier and minimums.">
        <Field id="ws-revenue" label="Annual revenue (USD)"><Select id="ws-revenue" value={f.annual_revenue} onChange={(v) => set({ annual_revenue: v })} options={REVENUE} /></Field>
        <Field id="ws-volume" label="Expected monthly order volume"><Select id="ws-volume" value={f.monthly_volume} onChange={(v) => set({ monthly_volume: v })} options={VOLUME} /></Field>
        <Field id="ws-brands" label="Brands you currently carry (optional)" full><input id="ws-brands" data-testid="ws-brands" className={input} placeholder="e.g. Qamis Co, Sunnah Supply…" value={f.brands_carried} onChange={(e) => set({ brands_carried: e.target.value })} /></Field>
        <Field id="ws-customer" label="Who is your customer? (optional)" full><textarea id="ws-customer" data-testid="ws-customer" className={area} placeholder="Age range, community, style, price point…" value={f.customer_profile} onChange={(e) => set({ customer_profile: e.target.value })} /></Field>
      </Section>

      <Section n={4} title="Where you'll sell" sub="Tick every channel that applies, then tell us the specifics.">
        <div className="sm:col-span-2">
          <span className={lbl}>Sales channels</span>
          <div data-testid="ws-channels" className="flex flex-wrap gap-2">
            {CHANNELS.map((c) => {
              const on = f.sales_channels.includes(c);
              return (
                <button key={c} type="button" data-testid={`ws-channel-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`} aria-pressed={on} onClick={() => toggleChannel(c)} className={`h-10 px-4 rounded-full border font-mono-d text-[10px] uppercase tracking-[0.15em] transition-colors ${on ? "bg-[#0a0a0b] text-white border-[#0a0a0b]" : "bg-[#ffffff] border-[#e3e2dc] hover:border-[#ff2a85]"}`}>
                  {on && <Check size={11} className="inline mr-1.5 -mt-0.5" strokeWidth={3} />}{c}
                </button>
              );
            })}
          </div>
        </div>
        <Field id="ws-where" label="Where exactly will Deen District be sold?" full><textarea id="ws-where" data-testid="ws-where" className={area} required minLength={3} placeholder="Store names and neighbourhoods, website URL, event calendar, marketplaces…" value={f.where_sell} onChange={(e) => set({ where_sell: e.target.value })} /></Field>
        <Field id="ws-timeline" label="When would you like to launch?"><Select id="ws-timeline" value={f.launch_timeline} onChange={(v) => set({ launch_timeline: v })} options={TIMELINE} /></Field>
      </Section>

      <Section n={5} title="Why Deen District" sub="The brand is built on Deen over Dunya — tell us why it fits your space.">
        <Field id="ws-why" label="Why do you want to carry Deen District?" full><textarea id="ws-why" data-testid="ws-why" className={area} required minLength={5} value={f.why_deen} onChange={(e) => set({ why_deen: e.target.value })} /></Field>
        <Field id="ws-notes" label="Anything else? (optional)" full><textarea id="ws-notes" data-testid="ws-notes" className={area} value={f.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
      </Section>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
        <button data-testid="ws-submit" type="submit" disabled={state === "sending"} className="h-14 px-10 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
          {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : "Submit application"}
        </button>
        <p className="text-xs text-[#5f5e5a] leading-relaxed">We'll reply by email within 3-5 business days. Your details are only used to evaluate your application.</p>
      </div>
    </form>
  );
};
